










































































import { FormMessage, FormMessageAttribute } from '@/types/Form.interface'
import Group from '@/types/Group.interface'
import Vue from 'vue'
import moment from 'moment-timezone'
import FormMessages from '@/components/FormMessages.vue'
import GroupSelector from '@/components/GroupSelector.vue'
export default Vue.extend({
    components: { FormMessages, GroupSelector },
    props: {
        campaign: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            //  The color used to style the component
            color: null,
            //  The group attached to the campaign
            group: null,
            //  A temporary clone of the campaign
            temp: null
        }
    },
    mounted() {
        //  If the campaign has an assigned group, fetch it
        if (this.campaign.group_id) this.getGroup()

        //  Clone the original data (so we can revert changes if the user cancels the update)
        this.temp = window._.cloneDeep(this.campaign)
    },
    computed: {
        //  Determines if the Keyword, Name, and Group inputs are disabled
        disabled(): boolean {
            //  If the campaign has a required flag set true, disable the inputs
            if (this.campaign.required > 0) return true

            //  Else, the inputs are enabled
            return false
        },
        subscribeMessage(): FormMessage[] {
            return [
                {
                    attribute: FormMessageAttribute.subscribe_message,
                    text: this.temp.subscribe_message,
                    media: this.temp.subscribe_media
                }
            ] as FormMessage[]
        },
        unsubscribeMessage(): FormMessage[] {
            return [
                {
                    attribute: FormMessageAttribute.unsubscribe_message,
                    text: this.temp.unsubscribe_message,
                    media: this.temp.unsubscribe_media
                }
            ] as FormMessage[]
        }
    },
    methods: {
        async getGroup(): Promise<void> {
            //  Send the request to the API
            const group = await this.$store.dispatch('groupsFind', this.campaign.group_id)

            //  Set the current group to the response (null/group)
            this.group = group
        },
        onGroupSelected(group: Group): void {
            //  If this is a smart group, pop a dialog warning the user to snapshot it
            if (group.query) {
                //  Clear the query group from the group selector
                this.$refs.groupSelector.clear()

                //  Inform the user that they cannot do this
                this.$store.dispatch('dialogOpen', {
                    type: 'CONFIRM',
                    title: 'Filter Groups Disabled',
                    text: `Filter Groups must be duplicated into a simple group before being attached to an autoresponder. Do you wish to proceed?`,
                    icon: 'mdi-alert',
                    color: 'yellow',
                    callback: (result) => {
                        //  If the user selected 'NO' in the dialog, do nothing
                        if (!result) return

                        //  Else, snapshot the query group
                        this.snapshotGroup(group)
                    }
                })
            }
            //  Attach the selected group to the campaign
            else this.temp.group_id = group.id
        },
        popGroupHelp(): void {
            this.$store.dispatch('dialogOpen', {
                type: 'MESSAGE',
                title: 'Assigned Group',
                text: `When a contact texts your autoresponder keyword, they'll get added to a group. Select the group you'd like associated with this autoresponder.`,
                icon: 'mdi-account-multiple',
                color: 'primary'
            })
        },
        popSubscribeHelp(): void {
            this.$store.dispatch('dialogOpen', {
                type: 'MESSAGE',
                title: 'Subscribe Keyword',
                text: `Keywords trigger an autoresponse. When a contact texts your MessageDesk number with the given keyword, they'll automatically get a response.`,
                icon: 'mdi-key',
                color: 'primary'
            })
        },
        popSubscribeMessageHelp(): void {
            this.$store.dispatch('dialogOpen', {
                type: 'MESSAGE',
                title: 'Keyword Message',
                text: `This is the autoresponse a contact will receive once they text your keyword.`,
                icon: 'mdi-message-reply-text',
                color: 'primary'
            })
        },
        popNameHelp(): void {
            this.$store.dispatch('dialogOpen', {
                type: 'MESSAGE',
                title: 'Name',
                text: `Give your autoresponder a descriptive name. This is only for your own reference.`,
                icon: 'mdi-card-text',
                color: 'primary'
            })
        },
        popUnsubscribeHelp(): void {
            this.$store.dispatch('dialogOpen', {
                type: 'MESSAGE',
                title: 'Unsubscribe Keyword',
                text: `When a contact texts this keyword, they'll automatically stop receiving messages from this keyword group.`,
                icon: 'mdi-key',
                color: 'primary'
            })
        },
        popUnsubscribeMessageHelp(): void {
            this.$store.dispatch('dialogOpen', {
                type: 'MESSAGE',
                title: 'Unsubscribe Message',
                text: `This is the autoresponse a contact will receive once they text your unsubscribe keyword.`,
                icon: 'mdi-message-reply-text',
                color: 'primary'
            })
        },
        setUpdatedMessages(): void {
            //  Set our temp subscribe message and media to our submission
            this.temp.subscribe_message = this.subscribeMessage[0].text
            this.temp.subscribe_message_media_id =
                this.subscribeMessage[0].media?.id || null

            //  Set our temp unsubscribe message and media to our submission
            this.temp.unsubscribe_message = this.unsubscribeMessage[0].text
            this.temp.unsubscribe_message_media_id =
                this.unsubscribeMessage[0].media?.id || null
        },
        async snapshotGroup(group: Group): Promise<void> {
            //  Get the group members count
            const members = group.persons_count

            //  If the group has no members, pop 'No Contacts'
            if (!members) {
                return this.$store.dispatch('dialogOpen', {
                    type: 'MESSAGE',
                    title: 'Group Has No Contacts',
                    text: 'We cannot duplicate a group with no members.',
                    icon: 'mdi-alert',
                    color: 'red'
                })
            }

            //  Else, create a formatted date, to use in our snapshot description
            const date = moment().format('MM/DD/YYYY h:mm:ss')

            //  Assemble the snapshot group name
            let name = `Autoresponder: ${group.name} ${date}`

            //  If the group name is too long, truncate it
            if (name.length > 45) name = name.substr(0, 41) + '...'

            //  Create a request payload by cloning of the selected group
            const payload = { ...group }

            //  Attach 'snapshot' properties to the payload
            payload.name = name
            payload.description = `A duplicate group of ${group.name} created on ${date}.`
            payload.snapshot = true

            //  Post the snapshot to the API
            const snapshot = await this.$store.dispatch('groupsPost', payload)

            //  If it failed, do nothing
            if (!snapshot) return

            //  Set the selected group in the group selector
            this.group = snapshot

            //  Attach the new snapshot group to the campaign
            this.temp.group_id = snapshot.id

            //  Otherwise, inform the user of the newly created snapshot group
            this.$store.dispatch('dialogOpen', {
                type: 'MESSAGE',
                title: `Success`,
                text: `A duplicate of your smart group ${group.name} has been successfully created. You can find it in the list of groups as ${snapshot.name}.`,
                icon: 'mdi-check',
                color: 'primary'
            })
        },
        async submit(): Promise<void> {
            //  Set the subscribed and unsubscribe messages
            this.setUpdatedMessages()

            //  Validate the form
            const valid = await this.$validator.validateAll()

            //  If the form is NOT valid, do nothing
            if (!valid) return

            //  Else, emit changes to the parent
            this.$emit('submit', this.temp)
        }
    }
})
